import { defineStore } from "pinia";
import {
  collection,
  query,
  getFirestore,
  where,
  getDocs,
  getDoc,
  startAfter,
  limit,
  orderBy,
  doc,
  setDoc,
  Timestamp,
  updateDoc,
  deleteDoc,
  runTransaction
} from "firebase/firestore";
import { Ref, ref } from "vue";
import { userUserStore } from "../stores/user";
import { useMesasStore } from "./mesas";
import { cambiarEstadoPedido } from "./cloudFunctions/cambiarEstadoPedido";
import {
  formatearFechaAAAAMMDD,
  utilFechaGetCurrentTime
} from "../utils/fecha";
import {
  Pedido,
  FiltroPedidos,
  pedidoItemConverter,
  pedidoConverter
} from "@fixeduy/restaurantestypes";
import type { PedidoItem } from "@fixeduy/restaurantestypes";
import { utilFechaGetTimestamp } from "../utils/fechaFixedTypes";

export const usePedidosStore = defineStore("pedidos", () => {
  const db = getFirestore();
  const userStore = userUserStore();
  const pedidos: Ref<Pedido[]> = ref([]);
  let last: any = null;
  let mostrar = 200;
  const cargando = ref(false);
  const showExitoMsg = ref(false);
  const buscar = ref<string>("");
  let procesandoGetPedidos = false;
  let ultimaCargaVacia = false;
  let ejecutarVaciarFiltros = ref(true);

  const filtros: Ref<FiltroPedidos> = ref({
    fechaInicio: "",
    fechaFin: "",
    horaInicio: "",
    horaFin: "",
    cliente: null,
    numero: "",
    origen: "",
    estado: ""
  });
  const filtrado = ref(false);

  const parseHoraMinuto = (
    hora: string | null | undefined,
    porDefecto: { h: number; m: number }
  ) => {
    const source = (hora ?? "").trim();
    const match = source.match(/(\d{1,2}):(\d{2})/);
    if (!match) return porDefecto;

    const h = Number(match[1]);
    const m = Number(match[2]);
    if (!Number.isFinite(h) || !Number.isFinite(m)) return porDefecto;
    if (h < 0 || h > 23 || m < 0 || m > 59) return porDefecto;

    return { h, m };
  };

  const getTimestampDesdeFechaHora = (
    fechaDDMMYYYY: string,
    hora: string | null | undefined,
    esFin: boolean
  ) => {
    const fechaNormalizada = formatearFechaAAAAMMDD(fechaDDMMYYYY);
    const [yyyy, mm, dd] = fechaNormalizada.split("-").map(Number);
    const horaMinuto = parseHoraMinuto(
      hora,
      esFin ? { h: 23, m: 59 } : { h: 0, m: 0 }
    );

    return utilFechaGetTimestamp(
      yyyy,
      mm,
      dd,
      horaMinuto.h,
      horaMinuto.m,
      esFin ? 59 : 0
    );
  };

  const firestoreQuery = () => {
    let fechaInicio: number | string = "";
    let fechaFin: number | string = "";

    if (
      filtros.value.fechaInicio != "" &&
      filtros.value.fechaInicio != "null"
    ) {
      fechaInicio = getTimestampDesdeFechaHora(
        filtros.value.fechaInicio,
        filtros.value.horaInicio,
        false
      );
    }

    if (filtros.value.fechaFin != "" && filtros.value.fechaFin != "null") {
      fechaFin = getTimestampDesdeFechaHora(
        filtros.value.fechaFin,
        filtros.value.horaFin,
        true
      );
    }
    const collectionRoute = `empresas/${userStore.empresa!.id}/pedidos/`;
    // const collectionRoute = `empresas/WwlMlToZLPVaBD4kIcYUQuhta3A3/pedidos/`;
    return query(
      collection(db, collectionRoute),
      ...(fechaInicio != "" ? [where("fechaCreacion", ">=", fechaInicio)] : []),
      ...(fechaFin != "" ? [where("fechaCreacion", "<=", fechaFin)] : []),
      ...(filtros.value.numero != ""
        ? [where("numero", "==", parseInt(filtros.value.numero))]
        : []),
      ...(filtros.value.estado != ""
        ? [where("estado", "==", filtros.value.estado)]
        : []),
      ...(filtros.value.origen != ""
        ? [where("origen", "==", filtros.value.origen)]
        : []),
      orderBy("fechaCreacion", "desc"),
      orderBy("numero", "desc"),
      ...(last != null ? [startAfter(last)] : []),
      ...(mostrar > 0 ? [limit(mostrar)] : [])
    ).withConverter(pedidoConverter);
  };

  const init = () => {
    last = null;
    // filtrado.value = false;
    ultimaCargaVacia = false;
    pedidos.value = [];
    cargando.value = true;
    if (ejecutarVaciarFiltros.value) vaciarFiltros();
    else {
      ejecutarVaciarFiltros.value = true;
      return filtrarPedidos();
    }
    getPedidosReordenarReCalcular();
    getPedidos();
  };

  const vaciarFiltros = async () => {
    ((filtros.value.fechaInicio = ""),
      (filtros.value.fechaFin = ""),
      (filtros.value.horaInicio = ""),
      (filtros.value.horaFin = ""),
      (filtros.value.cliente = null),
      (filtros.value.numero = ""),
      (filtros.value.estado = ""),
      (filtros.value.origen = ""));
  };

  const getPedidos = async () => {
    if (procesandoGetPedidos || ultimaCargaVacia) return;
    procesandoGetPedidos = true;

    const querySnapshot = await getDocs(firestoreQuery());
    if (querySnapshot.docs[querySnapshot.docs.length - 1] != null) {
      last = querySnapshot.docs[querySnapshot.docs.length - 1];
    }
    // if (filtros.value.horaInicio || filtros.value.horaFin) {
    //   const [hInicio, mInicio] = filtros.value.horaInicio
    //     ? filtros.value.horaInicio.split(":").map(Number)
    //     : [0, 0];
    //   const [hFin, mFin] = filtros.value.horaFin
    //     ? filtros.value.horaFin.split(":").map(Number)
    //     : [23, 59];

    //   querySnapshot.docs.forEach(doc => {
    //     const data = doc.data();
    //     const fecha: Date = (
    //       data.fechaCreacion as unknown as Timestamp
    //     ).toDate();
    //     const horaDoc = fecha.getHours();
    //     const minDoc = fecha.getMinutes();

    //     const horaNumDoc = horaDoc * 100 + minDoc;
    //     const horaNumInicio = hInicio * 100 + mInicio;
    //     const horaNumFin = hFin * 100 + mFin;

    //     if (horaNumDoc >= horaNumInicio && horaNumDoc <= horaNumFin) {
    //       pedidos.value.push(data); // ✅ acá sí pushea correctamente
    //     }
    //   });
    // } else {
    querySnapshot.forEach(doc => {
      pedidos.value.push(doc.data());
    });
    //    }

    ultimaCargaVacia = querySnapshot.docs.length == 0;

    procesandoGetPedidos = false;
    cargando.value = false;
  };

  const getPedidosReordenarReCalcular = () => {
    pedidos.value = Object.values(
      pedidos.value.reduce(
        (acc, item) => {
          acc[item.id] = item;
          return acc;
        },
        {} as { [key: string]: Pedido }
      )
    );

    pedidos.value.sort((a, b) => {
      return b.fechaCreacion - a.fechaCreacion;
    });
  };

  const getSiguienteNumero = async (): Promise<number> => {
    const collectionRoute = `empresas/${userStore.empresa!.id}/pedidos`;
    const q = query(
      collection(db, collectionRoute),
      orderBy("numero", "desc"),
      limit(1)
    ).withConverter(pedidoConverter);
    const snapshot = await getDocs(q);
    if (snapshot.empty) return 1;
    const ultimo = snapshot.docs[0].data();
    return (ultimo.numero ?? 0) + 1;
  };

  // const crearPedido = async (datos: Pedido) => {
  //   const empresaId = userStore.empresa?.id;
  //   if (!empresaId) throw new Error("No hay empresa seleccionada");

  //   const ahora = utilFechaGetCurrentTime();
  //   const numero = await getSiguienteNumero();
  //   const collectionRoute = `empresas/${empresaId}/pedidos`;
  //   const docRef = doc(collection(db, collectionRoute));

  //   const pedido: Pedido = {
  //     ...datos,
  //     version: 1,
  //     id: docRef.id,
  //     numero,
  //     fechaCreacion: ahora,
  //     fechaActualizacion: ahora,
  //     fechaCierre: 0
  //   };

  //   await setDoc(docRef, pedido);
  //   ultimaCargaVacia = false;
  //   pedidos.value.unshift(pedido);
  //   getPedidosReordenarReCalcular();
  //   return pedido;
  // };
  const crearPedido = async (datos: Pedido) => {
    const empresaId = userStore.empresa?.id;
    if (!empresaId) throw new Error("No hay empresa seleccionada");

    const ahora = utilFechaGetCurrentTime();
    const pedidosCollectionRoute = `empresas/${empresaId}/pedidos`;
    const contadorRef = doc(
      db,
      `empresas/${empresaId}/metadata/numeracionPedidos`
    );
    const nuevoPedidoRef = doc(collection(db, pedidosCollectionRoute));

    const pedidoCreado = await runTransaction(db, async transaction => {
      const contadorSnap = await transaction.get(contadorRef);

      const ultimoPedidoNumero = contadorSnap.exists()
        ? (contadorSnap.data()?.ultimoPedidoNumero ?? 0)
        : 0;

      const numero = ultimoPedidoNumero + 1;

      transaction.set(
        contadorRef,
        { ultimoPedidoNumero: numero },
        { merge: true }
      );

      const pedido: Pedido = {
        ...datos,
        version: 1,
        id: nuevoPedidoRef.id,
        numero,
        fechaCreacion: ahora,
        fechaActualizacion: ahora,
        fechaCierre: 0
      };

      transaction.set(nuevoPedidoRef, pedido);

      return pedido;
    });

    ultimaCargaVacia = false;
    pedidos.value.unshift(pedidoCreado);
    getPedidosReordenarReCalcular();

    return pedidoCreado;
  };

  const filtrarPedidos = async () => {
    ultimaCargaVacia = false;
    cargando.value = true;
    pedidos.value = [];
    last = null;
    mostrar = 200;
    let i = 0;
    while (ultimaCargaVacia == false) {
      await getPedidos();
      i++;
      if (i >= 50) {
        break;
      }
    }
    getPedidosReordenarReCalcular();
    cargando.value = false;
    mostrar = 10;
  };

  const getPedidoById = async (pedidoId: string) => {
    const collectionRoute = `empresas/${userStore.empresa!.id}/pedidos/${pedidoId}/pedidos-items`;

    try {
      const colRef = collection(db, collectionRoute).withConverter(
        pedidoItemConverter
      );

      const snap = await getDocs(colRef);

      return snap.docs.map(d => d.data());
    } catch (e) {
      console.error("Error en la consulta: ", e);
      throw e;
    }
  };

  const anularPedido = async (Pedido: Pedido) => {
    // try {
    //   const resultado = await PedidoAnular({
    //     Pedido: Pedido
    //   });
    //   return resultado;
    // } catch (e) {
    //   throw e;
    // }
  };

  const limparPedidos = () => {
    pedidos.value = [];
    vaciarFiltros();
  };

  const modificarEstado = async (
    idPedido: string,
    estado: Pedido["estado"]
  ): Promise<true | string> => {
    const empresaId = userStore.empresa?.id;
    if (!empresaId) return "No hay empresa seleccionada.";

    try {
      // Toda la lógica de transición (y actualización de mesa) pasa a la callable.
      const result = await cambiarEstadoPedido({
        pedidoId: idPedido,
        estado: estado as unknown as string
      });

      if (!result.exito) return result.errorMsg;

      // Best-effort: sincronizamos UI local leyendo desde Firestore.
      const pedidoCollectionRoute = `empresas/${empresaId}/pedidos`;
      const pedidoRef = doc(db, pedidoCollectionRoute, idPedido).withConverter(
        pedidoConverter
      );
      const pedidoSnap = await getDoc(pedidoRef);
      if (!pedidoSnap.exists()) return "El pedido no existe o fue eliminado.";

      const pedidoActual = pedidoSnap.data() as Pedido;
      const idx = pedidos.value.findIndex(p => p.id === idPedido);
      if (idx !== -1) {
        pedidos.value[idx] = { ...pedidoActual, id: idPedido };
      }

      const mesaId = pedidoActual.mesaId as string | null | undefined;
      if (mesaId) {
        const mesaRef = doc(db, `empresas/${empresaId}/mesas`, mesaId);
        const mesaSnap = await getDoc(mesaRef);

        if (mesaSnap.exists()) {
          const mesaData = mesaSnap.data() as any;
          const mesasStore = useMesasStore();
          const mesaIdx = mesasStore.mesas.findIndex(m => m.id === mesaId);
          if (mesaIdx !== -1) {
            mesasStore.mesas[mesaIdx] = {
              ...mesasStore.mesas[mesaIdx],
              ...mesaData
            };
          }
        }
      }

      return true;
    } catch (error: any) {
      console.error("Error al modificar estado", error);

      if (error?.code === "permission-denied") {
        return "No tenés permisos para modificar el estado del pedido.";
      }

      if (error?.code === "not-found") {
        return "El pedido no existe o fue eliminado.";
      }

      if (typeof error?.message === "string") {
        return error.message;
      }

      return "Ocurrió un error al modificar el estado del pedido. Intenta nuevamente.";
    }
  };

  const modificarEstadoItem = async (
    idPedido: string,
    idItem: string,
    estado: string
  ): Promise<true | string> => {
    try {
      const collectionRoute = `empresas/${userStore.empresa!.id}/pedidos/${idPedido}/pedidos-items`;
      const itemRef = doc(db, collectionRoute, idItem);

      await updateDoc(itemRef, {
        estado
      });

      return true;
    } catch (error: any) {
      console.error("Error al modificar estado del ítem", error);

      if (error?.code === "permission-denied") {
        return "No tenés permisos para modificar el estado del ítem.";
      }

      if (error?.code === "not-found") {
        return "El ítem no existe o fue eliminado.";
      }

      return "Ocurrió un error al modificar el estado del ítem. Intenta nuevamente.";
    }
  };

  const crearPedidoItem = async (
    pedidoId: string,
    item: Omit<PedidoItem, "id" | "pedidoId">
  ): Promise<PedidoItem> => {
    const empresaId = userStore.empresa?.id;
    if (!empresaId) throw new Error("No hay empresa seleccionada");

    const collectionRoute = `empresas/${empresaId}/pedidos/${pedidoId}/pedidos-items`;
    const docRef = doc(collection(db, collectionRoute));

    const nuevo: PedidoItem = {
      ...item,
      id: docRef.id,
      pedidoId
    };

    await setDoc(docRef, nuevo);
    return nuevo;
  };

  const updateTotalesPedido = async (
    pedidoId: string,
    datos: Pick<Pedido, "subtotal" | "montoIva" | "total" | "estado">
  ): Promise<boolean> => {
    const empresaId = userStore.empresa?.id;
    if (!empresaId) return false;

    const collectionRoute = `empresas/${empresaId}/pedidos/`;
    const refDoc = doc(db, collectionRoute, pedidoId);
    try {
      await updateDoc(refDoc, {
        ...datos,
        fechaActualizacion: utilFechaGetCurrentTime()
      });
      const idx = pedidos.value.findIndex(p => p.id === pedidoId);
      if (idx !== -1) {
        pedidos.value[idx] = { ...pedidos.value[idx], ...datos } as Pedido;
      }
      return true;
    } catch (error) {
      console.error("Error al actualizar totales del pedido", error);
      return false;
    }
  };

  const updatePedido = async (
    pedidoId: string,
    datos: Partial<
      Pick<
        Pedido,
        | "estado"
        | "observaciones"
        | "subtotal"
        | "montoIva"
        | "total"
        | "fechaCierre"
        | "tipoDescuento"
        | "descuentoMonto"
        | "descuentoPorcentaje"
        | "mesaId"
        | "mesaNombre"
        | "mesaCapacidad"
        | "cfeId"
        | "cfeNumero"
        | "cfeSerie"
        | "cfeTipo"
        | "ventaId"
        | "facturado"
        | "devuelto"
        | "cliente"
        | "cfeIdDevolucion"
        | "cfeNumeroDevolucion"
        | "cfeSerieDevolucion"
        | "cfeTipoDevolucion"
        | "ventaIdDevolucion"
      >
    >
  ): Promise<boolean> => {
    const empresaId = userStore.empresa?.id;
    if (!empresaId) return false;

    const collectionRoute = `empresas/${empresaId}/pedidos/`;
    const refDoc = doc(db, collectionRoute, pedidoId);
    try {
      await updateDoc(refDoc, {
        ...datos,
        fechaActualizacion: utilFechaGetCurrentTime()
      });
      const idx = pedidos.value.findIndex(p => p.id === pedidoId);
      if (idx !== -1) {
        pedidos.value[idx] = { ...pedidos.value[idx], ...datos } as Pedido;
      }
      return true;
    } catch (error) {
      console.error("Error al actualizar pedido", error);
      return false;
    }
  };

  const getPedidosPorMesaId = async (mesaId: string): Promise<Pedido[]> => {
    const empresaId = userStore.empresa?.id;
    if (!empresaId) return [];

    const collectionRoute = `empresas/${empresaId}/pedidos/`;

    // Filtramos en cliente por estado para evitar limitaciones de Firestore en 'in'.
    const q = query(
      collection(db, collectionRoute),
      where("mesaId", "==", mesaId)
    ).withConverter(pedidoConverter);

    const snap = await getDocs(q);
    return snap.docs.map(d => d.data());
  };

  const enviarPendientesPedidoItems = async (
    pedidoId: string,
    nuevoEstado: string = "enviada"
  ): Promise<number> => {
    const empresaId = userStore.empresa?.id;
    if (!empresaId) return 0;

    const collectionRoute = `empresas/${empresaId}/pedidos/${pedidoId}/pedidos-items`;

    const q = query(
      collection(db, collectionRoute),
      where("estado", "==", "pendiente")
    ).withConverter(pedidoItemConverter);

    const snap = await getDocs(q);

    let count = 0;
    await Promise.all(
      snap.docs.map(async docSnap => {
        await updateDoc(docSnap.ref, { estado: nuevoEstado });
        count += 1;
      })
    );

    return count;
  };

  const borrarPedidoItem = async (
    pedidoId: string,
    itemId: string
  ): Promise<boolean> => {
    const empresaId = userStore.empresa?.id;
    if (!empresaId) return false;

    const collectionRoute = `empresas/${empresaId}/pedidos/${pedidoId}/pedidos-items`;
    const itemRef = doc(db, collectionRoute, itemId);

    try {
      await deleteDoc(itemRef);
      return true;
    } catch (error) {
      console.error("Error al borrar PedidoItem", error);
      return false;
    }
  };

  const updatePedidoItem = async (
    pedidoId: string,
    itemId: string,
    datos: Partial<
      Pick<
        PedidoItem,
        | "cantidad"
        | "observaciones"
        | "subtotal"
        | "montoIva"
        | "total"
        | "descuentoMonto"
      >
    >
  ): Promise<boolean> => {
    const empresaId = userStore.empresa?.id;
    if (!empresaId) return false;

    const collectionRoute = `empresas/${empresaId}/pedidos/${pedidoId}/pedidos-items`;
    const itemRef = doc(db, collectionRoute, itemId);

    try {
      await updateDoc(itemRef, { ...datos });
      return true;
    } catch (error) {
      console.error("Error al actualizar PedidoItem", error);
      return false;
    }
  };

  return {
    init,
    getPedidos,
    pedidos,
    filtrarPedidos,
    crearPedido,
    getSiguienteNumero,
    getPedidoById,
    anularPedido,
    filtros,
    ejecutarVaciarFiltros,
    modificarEstado,
    modificarEstadoItem,
    crearPedidoItem,
    updateTotalesPedido,
    updatePedido,
    getPedidosPorMesaId,
    enviarPendientesPedidoItems,
    borrarPedidoItem,
    updatePedidoItem
  };
});
